import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    makeStyles,
    Typography,
    useMediaQuery,
    useTheme,
} from "@material-ui/core"
import { ArrowBack, DragIndicator } from "@material-ui/icons"
import { useSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { salvarCardapioCompleto } from "../../../services/cardapio"
import useLoadingStore from "../../../stores/LoadingStore"
import { useCardapioContext } from "./CardapioContext"

const useStyles = makeStyles(theme => ({
    card: {
        display: "flex",
        alignItems: "center",
        width: "100%",
        padding: "8px 10px",
        border: "1px solid #e0e0e0",
        borderRadius: 8,
        marginBottom: 4,
        backgroundColor: "white",
        userSelect: "none",
        transition: "background-color 0.1s",
        "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#bdbdbd",
        },
    },
    cardDragging: {
        opacity: 0.35,
        border: "1px dashed #1976d2",
    },
    dragHandle: {
        color: "#9e9e9e",
        marginRight: 8,
        cursor: "grab",
        flexShrink: 0,
        "&:active": { cursor: "grabbing" },
    },
    nome: {
        flex: 1,
        fontSize: "0.9rem",
    },
    breadcrumb: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: theme.spacing(1.5),
        padding: "4px 0",
        borderBottom: "1px solid #f0f0f0",
    },
    breadcrumbTexto: {
        fontSize: "0.8rem",
        color: "#666",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    vazio: {
        textAlign: "center",
        color: "#9e9e9e",
        padding: theme.spacing(3),
    },
    botaoSalvar: {
        backgroundColor: "#14A44D",
        color: "white",
        "&:hover": { backgroundColor: "#28B76C" },
    },
    nivel: {
        fontSize: "0.75rem",
        color: "#1976d2",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: theme.spacing(1),
    },
}))

function getArrayNaArvore(arvore, caminho) {
    if (caminho.length === 0) return arvore
    const cat = arvore.find(c => c.id === caminho[0].id)
    if (!cat) return []
    if (caminho.length === 1) return cat.produtos ?? []
    const prod = cat.produtos?.find(p => p.id === caminho[1].id)
    if (!prod) return []
    if (caminho.length === 2) return prod.opcoes ?? []
    const grupo = prod.opcoes?.find(g => g.id === caminho[2].id)
    if (!grupo) return []
    return grupo.opcoes ?? []
}

function temFilhos(item, nivelAtual) {
    if (nivelAtual === 0) return (item.produtos?.length ?? 0) > 0
    if (nivelAtual === 1) return (item.opcoes?.length ?? 0) > 0
    if (nivelAtual === 2) return (item.opcoes?.length ?? 0) > 0
    return false
}

export default function DialogReordenar({ open, onClose }) {
    const classes = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const { cardapio, categorias, setCategorias, estabelecimento } = useCardapioContext()
    const setLoading = useLoadingStore(s => s.setLoading)
    const { enqueueSnackbar } = useSnackbar()
    const alertStart = (msg, tipo) => enqueueSnackbar(msg, { variant: tipo })
    const autorizacao = localStorage.getItem("chaveSistema")

    const [localCategorias, setLocalCategorias] = useState([])
    const [caminho, setCaminho] = useState([])
    const [arrastando, setArrastando] = useState(null)

    useEffect(() => {
        if (open && categorias) {
            const copia = JSON.parse(JSON.stringify(categorias))
            copia.sort((a, b) => a.ordemExib - b.ordemExib)
            setLocalCategorias(copia)
            setCaminho([])
            setArrastando(null)
        }
    }, [open, categorias])

    const itensAtuais = () => {
        const arr = getArrayNaArvore(localCategorias, caminho)
        return [...arr].sort((a, b) => a.ordemExib - b.ordemExib)
    }

    const handleDragOver = hoverIndex => {
        if (arrastando === null || arrastando === hoverIndex) return

        setLocalCategorias(prev => {
            const arvore = JSON.parse(JSON.stringify(prev))
            const arr = getArrayNaArvore(arvore, caminho)
            const itens = [...arr].sort((a, b) => a.ordemExib - b.ordemExib)

            const [removido] = itens.splice(arrastando, 1)
            itens.splice(hoverIndex, 0, removido)
            itens.forEach((item, i) => {
                item.ordemExib = i
            })

            if (caminho.length === 0) {
                return itens
            }
            const catIdx = arvore.findIndex(c => c.id === caminho[0].id)
            if (caminho.length === 1) {
                arvore[catIdx].produtos = itens
            } else {
                const prodIdx = arvore[catIdx].produtos.findIndex(p => p.id === caminho[1].id)
                if (caminho.length === 2) {
                    arvore[catIdx].produtos[prodIdx].opcoes = itens
                } else {
                    const grupoIdx = arvore[catIdx].produtos[prodIdx].opcoes.findIndex(
                        g => g.id === caminho[2].id
                    )
                    arvore[catIdx].produtos[prodIdx].opcoes[grupoIdx].opcoes = itens
                }
            }
            return arvore
        })

        setArrastando(hoverIndex)
    }

    const navegarPara = item => {
        if (!temFilhos(item, caminho.length)) return
        setCaminho(prev => [...prev, { id: item.id, nome: item.nome }])
        setArrastando(null)
    }

    const voltarNivel = () => {
        setCaminho(prev => prev.slice(0, -1))
        setArrastando(null)
    }

    const handleSalvar = async () => {
        setLoading(true)
        try {
            const data = { ...cardapio, categorias: localCategorias }
            const response = await salvarCardapioCompleto(estabelecimento.id, data, autorizacao)
            if (response.retornoErro) {
                alertStart(response.mensagem, "error")
            } else {
                setCategorias(localCategorias)
                alertStart("Ordem salva com sucesso", "success")
                onClose()
            }
        } catch {
            alertStart("Erro ao salvar ordem", "error")
        } finally {
            setLoading(false)
        }
    }

    const itens = itensAtuais()
    const nivelAtual = caminho.length

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
            <DialogTitle>Reordenar</DialogTitle>

            <DialogContent>
                {caminho.length > 0 && (
                    <div className={classes.breadcrumb}>
                        <IconButton size="small" onClick={voltarNivel}>
                            <ArrowBack fontSize="small" />
                        </IconButton>
                        <Typography className={classes.breadcrumbTexto}>
                            {caminho.map(p => p.nome).join(" › ")}
                        </Typography>
                    </div>
                )}

                {itens.length === 0 ? (
                    <Typography className={classes.vazio}>Nenhum item para reordenar</Typography>
                ) : (
                    <List disablePadding>
                        {itens.map((item, index) => (
                            <ListItem
                                key={item.id}
                                disableGutters
                                onClick={e => {
                                    if (temFilhos(item, nivelAtual)) {
                                        e.stopPropagation()
                                        navegarPara(item)
                                    }
                                }}
                            >
                                <div
                                    className={`${classes.card} ${
                                        arrastando === index ? classes.cardDragging : ""
                                    }`}
                                    draggable
                                    onDragStart={() => setArrastando(index)}
                                    onDragOver={e => {
                                        e.preventDefault()
                                        handleDragOver(index)
                                    }}
                                    onDragEnd={() => setArrastando(null)}
                                >
                                    <DragIndicator className={classes.dragHandle} />
                                    <Typography className={classes.nome}>{item.nome}</Typography>
                                </div>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
                <Button className={classes.botaoSalvar} variant="contained" onClick={handleSalvar}>
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
